import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AlertCircle, CheckCircle, Clock, MapPin, Truck, Car, Ambulance, Wrench, X } from 'lucide-react';
import { translations } from '../translations';

function TrackingAnimation({ language = 'en' }) {
  // Vehicle types and alert types defined with useMemo to prevent recreation
  const vehicleTypes = useMemo(() => [
    { color: '#3498DB', type: translations[language].fleetStatus.types.delivery, id: 'DEL' },
    { color: '#2ECC71', type: translations[language].fleetStatus.types.transport, id: 'TRN' },
    { color: '#E74C3C', type: translations[language].fleetStatus.types.emergency, id: 'EMG' },
    { color: '#F1C40F', type: translations[language].fleetStatus.types.service, id: 'SRV' }
  ], [language]);

  const alertTypes = useMemo(() => [
    { type: 'stop', message: translations[language].fleetStatus.alerts.stopped, icon: MapPin, color: '#3498DB' },
    { type: 'delay', message: translations[language].fleetStatus.alerts.delay, icon: Clock, color: '#E74C3C' },
    { type: 'complete', message: translations[language].fleetStatus.alerts.completed, icon: CheckCircle, color: '#2ECC71' },
    { type: 'alert', message: translations[language].fleetStatus.alerts.deviation, icon: AlertCircle, color: '#F1C40F' }
  ], [language]);

  // Initialize static vehicle IDs
  const staticVehicleIds = useMemo(() => {
    const ids = [];
    for (let i = 0; i < 12; i++) { // 6 routes × 2 vehicles per route
      const typeIndex = i % 4;
      const vehicleType = vehicleTypes[typeIndex];
      ids.push(`${vehicleType.id}-${String(i + 1).padStart(3, '0')}`);
    }
    return ids;
  }, [vehicleTypes]);

  const [alerts, setAlerts] = useState([]);
  const [activeAlertVehicles, setActiveAlertVehicles] = useState(new Set());
  
  // Initialize fleetStatus with proper structure
  const [fleetStatus, setFleetStatus] = useState({
    delivery: { total: 6, active: 6, stopped: 0, icon: Truck },
    transport: { total: 4, active: 3, stopped: 1, icon: Car },
    emergency: { total: 6, active: 5, stopped: 1, icon: Ambulance },
    service: { total: 7, active: 6, stopped: 1, icon: Wrench }
  });

  // Stop points where vehicles will pause
  const stopPoints = [
    { x: 300, y: 200, name: 'Commercial Hub', type: 'delivery' },
    { x: 800, y: 400, name: 'Residential Zone', type: 'delivery' },
    { x: 1200, y: 250, name: 'Industrial Park', type: 'transport' },
    { x: 500, y: 300, name: 'City Center', type: 'service' }
  ];

  // Simple building definitions - Google Maps style
  const buildings = [
    // Commercial district
    { x: 300, y: 200, width: 60, height: 60, color: '#E8EDF1', type: 'commercial' },
    { x: 380, y: 180, width: 50, height: 70, color: '#E8EDF1', type: 'commercial' },
    { x: 450, y: 210, width: 55, height: 55, color: '#E8EDF1', type: 'commercial' },
    // Residential area
    { x: 800, y: 400, width: 40, height: 40, color: '#F5F6F7', type: 'residential' },
    { x: 860, y: 420, width: 35, height: 35, color: '#F5F6F7', type: 'residential' },
    { x: 900, y: 390, width: 45, height: 45, color: '#F5F6F7', type: 'residential' },
    // Industrial zone
    { x: 1200, y: 250, width: 80, height: 100, color: '#E8EDF1', type: 'industrial' },
    { x: 1300, y: 270, width: 90, height: 70, color: '#E8EDF1', type: 'industrial' },
  ];

  // Zone definitions - Google Maps style
  const zones = [
    // Commercial district
    { 
      path: 'M 250,150 L 550,150 L 550,300 L 250,300 Z', 
      fill: '#E3F2FD',
      label: 'Commercial District'
    },
    // Residential area
    { 
      path: 'M 750,350 L 950,350 L 950,500 L 750,500 Z',
      fill: '#F1F8E9',
      label: 'Residential Area'
    },
    // Industrial zone
    { 
      path: 'M 1150,200 L 1400,200 L 1400,350 L 1150,350 Z',
      fill: '#FAFAFA',
      label: 'Industrial Zone'
    },
    // Green areas
    { 
      path: 'M 600,400 C 650,380 700,380 750,400 S 850,420 900,400',
      fill: '#E8F5E9',
      label: 'Park Area'
    }
  ];

  // Generate alerts for vehicles
  const generateAlert = useCallback(() => {
    const vehicleIndex = Math.floor(Math.random() * staticVehicleIds.length);
    const vehicleId = staticVehicleIds[vehicleIndex];
    const vehicleType = vehicleTypes[Math.floor(vehicleIndex / 3)];
    const alertType = alertTypes[Math.floor(Math.random() * alertTypes.length)];
    
    const newAlert = {
      id: Date.now(),
      vehicleId,
      vehicleType: vehicleType.type,
      message: alertType.message,
      icon: alertType.icon,
      color: alertType.color,
      timestamp: new Date().toLocaleTimeString()
    };

    setAlerts(prev => [newAlert, ...prev].slice(0, 5));
    setActiveAlertVehicles(prev => new Set([...prev, vehicleId]));

    // Remove the alert highlight after 5 seconds
    setTimeout(() => {
      setActiveAlertVehicles(prev => {
        const updated = new Set(prev);
        updated.delete(vehicleId);
        return updated;
      });
    }, 5000);

    // Update fleet status
    setFleetStatus(prev => {
      const type = vehicleType.type.toLowerCase();
      if (!prev[type]) return prev; // Guard clause for undefined type
      
      if (alertType.type === 'stop') {
        return {
          ...prev,
          [type]: {
            ...prev[type],
            active: Math.max(0, prev[type].active - 1),
            stopped: prev[type].stopped + 1
          }
        };
      } else if (alertType.type === 'complete') {
        return {
          ...prev,
          [type]: {
            ...prev[type],
            active: Math.min(prev[type].total, prev[type].active + 1),
            stopped: Math.max(0, prev[type].stopped - 1)
          }
        };
      }
      return prev;
    });
  }, [staticVehicleIds, vehicleTypes, alertTypes]);

  useEffect(() => {
    const interval = setInterval(generateAlert, 5000);
    return () => clearInterval(interval);
  }, [generateAlert]);

  return (
    <div className="relative w-full h-full min-h-[500px] overflow-hidden">
      {/* Light background overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-blue-50/20 z-10" />

      <svg
        viewBox="-200 0 2320 1080"
        className="w-full h-full scale-110 md:scale-100"
        style={{ 
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        }}
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          {/* Road pattern */}
          <pattern id="roadPattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <rect width="20" height="20" fill="#f8fafc" />
            <path d="M 0,10 L 20,10" stroke="#e2e8f0" strokeWidth="1" />
          </pattern>

          {/* Building gradient - subtle */}
          <linearGradient id="buildingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="100%" stopColor="#F8FAFC" />
          </linearGradient>
        </defs>

        {/* Background Grid - Google Maps style */}
        <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
          <path
            d="M 50 0 L 0 0 0 50"
            fill="none"
            stroke="rgba(226, 232, 240, 0.3)"
            strokeWidth="0.5"
          />
        </pattern>
        <rect width="100%" height="100%" fill="url(#grid)" />

        {/* Zones */}
        {zones.map((zone, i) => (
          <g key={`zone-${i}`}>
            <path
              d={zone.path}
              fill={zone.fill}
              opacity="0.4"
              className="transition-opacity duration-300"
            />
            {/* Zone labels */}
            <text
              x={zone.path.includes('C') ? 
                 parseInt(zone.path.split(' ')[1]) + 100 : 
                 parseInt(zone.path.split('L')[1]) + 50}
              y={zone.path.includes('C') ?
                 parseInt(zone.path.split(' ')[2]) + 20 :
                 parseInt(zone.path.split('L')[2]) + 30}
              fill="#64748b"
              fontSize="14"
              className="font-medium"
            >
              {zone.label}
            </text>
          </g>
        ))}

        {/* Simple Buildings */}
        {buildings.map((building, i) => (
          <g key={`building-${i}`}>
            {/* Simple building shape with subtle shadow */}
            <rect
              x={building.x}
              y={building.y}
              width={building.width}
              height={building.height}
              fill={building.color}
              className="transition-all duration-300"
              style={{
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
              }}
            />
          </g>
        ))}

        {/* Stop Points */}
        {stopPoints.map((point, i) => (
          <g key={`stop-${i}`}>
            <circle
              cx={point.x}
              cy={point.y}
              r="6"
              fill="#fff"
              stroke="#3498DB"
              strokeWidth="2"
              className="animate-pulse"
            />
            <text
              x={point.x + 10}
              y={point.y - 10}
              fill="#64748b"
              fontSize="12"
              className="font-medium"
            >
              {point.name}
            </text>
          </g>
        ))}

        {/* Routes with Vehicles */}
        {[
          `M -400,300 C 200,300 ${stopPoints[0].x},${stopPoints[0].y} ${stopPoints[1].x},${stopPoints[1].y} S 1800,300 2400,300`,
          `M -400,100 C 200,100 ${stopPoints[2].x},${stopPoints[2].y} ${stopPoints[3].x},${stopPoints[3].y} S 1800,100 2400,100`,
          `M -400,500 L 2400,500`,
          `M 200,0 L 200,1000`,
          `M 800,0 L 800,1000`,
          `M 1400,0 L 1400,1000`,
        ].map((path, i) => (
          <g key={i}>
            {/* Road base */}
            <path
              d={path}
              stroke="#f1f5f9"
              strokeWidth="20"
              fill="none"
            />
            {/* Road lines */}
            <path
              d={path}
              stroke="#e2e8f0"
              strokeWidth="1"
              strokeDasharray="10,10"
              fill="none"
            />
            
            {/* Hidden path for vehicle animation */}
            <path
              id={`route-${i}`}
              d={path}
              fill="none"
              stroke="none"
            />
            
            {/* Vehicles with alerts */}
            {[...Array(2)].map((_, j) => {
              const vehicleIndex = i * 2 + j;
              const vehicleId = staticVehicleIds[vehicleIndex];
              const vehicleType = vehicleTypes[Math.floor(vehicleIndex / 3)];
              const duration = 30 + i * 2;
              const hasAlert = activeAlertVehicles.has(vehicleId);
              
              return (
                <g key={`vehicle-${i}-${j}`}>
                  {/* Alert highlight circle */}
                  {hasAlert && (
                    <circle 
                      r="8" 
                      fill="none"
                      stroke={vehicleType.color}
                      strokeWidth="2"
                      className="animate-ping"
                    >
                      <animateMotion
                        dur={`${duration}s`}
                        repeatCount="indefinite"
                        begin={`${-j * duration / 2}s`}
                        keyPoints="0;0.3;0.3;0.6;0.6;1"
                        keyTimes="0;0.3;0.4;0.6;0.7;1"
                        calcMode="linear"
                      >
                        <mpath href={`#route-${i}`} />
                      </animateMotion>
                    </circle>
                  )}
                  
                  {/* Vehicle dot */}
                  <circle 
                    r={hasAlert ? "5" : "3"}
                    fill={vehicleType.color}
                    className={hasAlert ? "animate-pulse" : ""}
                  >
                    <animateMotion
                      dur={`${duration}s`}
                      repeatCount="indefinite"
                      begin={`${-j * duration / 2}s`}
                      keyPoints="0;0.3;0.3;0.6;0.6;1"
                      keyTimes="0;0.3;0.4;0.6;0.7;1"
                      calcMode="linear"
                    >
                      <mpath href={`#route-${i}`} />
                    </animateMotion>
                  </circle>
                  
                  {/* Vehicle info tooltip */}
                  <g style={{ opacity: hasAlert ? 1 : 0.95 }}>
                    <foreignObject 
                      width="120" 
                      height="50"
                      style={{
                        overflow: 'visible'
                      }}
                    >
                      <div className={`
                        bg-white shadow-sm px-2 py-1 rounded text-xs
                        ${hasAlert ? 'ring-2 ring-offset-2' : ''}
                      `}
                      style={{ 
                        ringColor: vehicleType.color,
                        transform: 'translate(-50%, -120%)'
                      }}
                      >
                        <div className="font-medium text-gray-700">{vehicleId}</div>
                        <div className="text-gray-500 text-[10px]">{vehicleType.type}</div>
                        {hasAlert && (
                          <div className="text-[10px] font-medium mt-1" style={{ color: vehicleType.color }}>
                            ● Alert Active
                          </div>
                        )}
                      </div>
                    </foreignObject>
                    <animateMotion
                      dur={`${duration}s`}
                      repeatCount="indefinite"
                      begin={`${-j * duration / 2}s`}
                      keyPoints="0;0.3;0.3;0.6;0.6;1"
                      keyTimes="0;0.3;0.4;0.6;0.7;1"
                      calcMode="linear"
                    >
                      <mpath href={`#route-${i}`} />
                    </animateMotion>
                  </g>
                </g>
              );
            })}
          </g>
        ))}
      </svg>

      {/* Live Fleet Status Panel - Desktop Only */}
      <div className="hidden md:block absolute top-4 left-4 bg-white/95 shadow-sm rounded-lg border border-gray-100 z-20 w-72">
        <div className="p-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-gray-700">
                {translations[language].fleetStatus.title}
              </span>
            </div>
            <span className="text-xs text-gray-500">{new Date().toLocaleTimeString()}</span>
          </div>
        </div>
        
        <div className="divide-y divide-gray-100">
          {Object.entries(fleetStatus).map(([type, data]) => (
            <div key={type} className="p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <data.icon 
                    className="w-4 h-4"
                    style={{ 
                      color: vehicleTypes.find(v => 
                        v.type.toLowerCase() === translations[language].fleetStatus.types[type].toLowerCase()
                      )?.color 
                    }}
                  />
                  <span className="text-sm font-medium text-gray-700 capitalize">
                    {translations[language].fleetStatus.types[type]}
                  </span>
                </div>
                <span className="text-xs font-medium text-gray-500">
                  {translations[language].fleetStatus.total}: {data.total}
                </span>
              </div>
              
              <div className="flex items-center space-x-2 mb-2">
                <div className="flex-1 bg-gray-100 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min((data.active / data.total) * 100, 100)}%`,
                      backgroundColor: vehicleTypes.find(v => 
                        v.type.toLowerCase() === translations[language].fleetStatus.types[type].toLowerCase()
                      )?.color
                    }}
                  />
                </div>
                <span className="text-xs text-gray-600 min-w-[60px] text-right">
                  {data.active} {translations[language].fleetStatus.active}
                </span>
              </div>
              
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{data.stopped} {translations[language].fleetStatus.stopped}</span>
                <span>{Math.min(((data.active / data.total) * 100), 100).toFixed(0)}% {translations[language].fleetStatus.utilization}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Summary Stats */}
        <div className="p-3 bg-gray-50 rounded-b-lg border-t border-gray-100">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-sm font-medium text-gray-900">
                {Object.values(fleetStatus).reduce((acc, curr) => acc + curr.active, 0)}
              </div>
              <div className="text-xs text-gray-500">{translations[language].fleetStatus.totalActive}</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-medium text-gray-900">
                {Object.values(fleetStatus).reduce((acc, curr) => acc + curr.total, 0)}
              </div>
              <div className="text-xs text-gray-500">{translations[language].fleetStatus.fleetSize}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts Panel - Desktop Only */}
      <div className="hidden md:block absolute top-4 right-4 w-80 bg-white/95 shadow-sm rounded-lg border border-gray-100 z-20 max-h-[400px] overflow-hidden">
        <div className="p-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {translations[language].fleetStatus.recentAlerts}
            </span>
            <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
          </div>
        </div>
        <div className="divide-y divide-gray-100 max-h-[320px] overflow-y-auto">
          {alerts.map(alert => (
            <div key={alert.id} className="p-3 hover:bg-gray-50 transition-colors">
              <div className="flex items-start space-x-3">
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${alert.color}15` }}
                >
                  <alert.icon className="w-4 h-4" style={{ color: alert.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">{alert.vehicleId}</p>
                    <span className="text-xs text-gray-500">{alert.timestamp}</span>
                  </div>
                  <p className="text-sm text-gray-600 truncate">{alert.message}</p>
                  <p className="text-xs text-gray-500">{alert.vehicleType}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default TrackingAnimation; 